import { useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import axios from './axios';
import { bentoTeal2, pureWhite } from './colors';
import constants from './constants';
import { notify } from './Notification';
import Loader from './Loader';
import ServicesDropdown from './ServicesDropdown';
import deepCopy from './utilities/deepCopy';
import formatUsd from './utilities/formatUsd';
import formatDateForRequest from './utilities/formatDateForRequest';
import getUrlPrefix from './utilities/getUrlPrefix';
import ToothQuadrant from './ToothQuadrant';
import PrimaryButton from './PrimaryButton';
import UsdTextField from './fields/UsdTextField';
import ServiceFrequencyInfo from './ServiceFrequencyInfo';
import TypographyError from './TypographyError';
import BentoIcon from './BentoIcon';
import ConfirmDialog from './ConfirmDialog';
import type {
  AddServicesProps,
  TeethState,
  ServiceOption,
  Service,
  ClaimExtract,
  ServiceData,
} from './AddServices.types';

const DEFAULT_TOOTH_STATE = {
  selected: false,
  surface_select: {
    distal: {
      selected: false,
    },
    occlusal: {
      selected: false,
    },
    buccal: {
      selected: false,
    },
    mesial: {
      selected: false,
    },
    lingual: {
      selected: false,
    },
  },
};

const generateDefaultTeethState = (): TeethState => {
  const teeth: TeethState = {};
  for (let i = 1; i <= 32; i++) {
    teeth[i] = deepCopy(DEFAULT_TOOTH_STATE);
  }
  for (const i of [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
  ]) {
    teeth[i] = deepCopy(DEFAULT_TOOTH_STATE);
  }
  return teeth;
};

const defaultQuadrantState = {
  upper_left: {
    selected: false,
  },
  upper_right: {
    selected: false,
  },
  lower_left: {
    selected: false,
  },
  lower_right: {
    selected: false,
  },
};

const defaultArchState = {
  upper: {
    selected: false,
  },
  lower: {
    selected: false,
  },
};

const styles = {
  error: {
    mt: 1,
    mx: 1.5,
    fontSize: '.75rem',
    lineHeight: '1em',
  },
  quadrants: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    mt: 2.5,
  },
  quadrant: {
    p: '10px 3px',
    fontSize: '14px',
    color: bentoTeal2,
    border: `1px solid ${bentoTeal2}`,
    textAlign: 'center',
    width: '49%',
    cursor: 'pointer',
    mb: 1,
  },
  arches: {
    width: '100%',
    mt: 1.25,
  },
  arch: {
    p: '10px 3px',
    fontSize: '14px',
    color: bentoTeal2,
    border: `1px solid ${bentoTeal2}`,
    textAlign: 'center',
    width: '100%',
    cursor: 'pointer',
    mb: 1,
  },
  selected: {
    color: pureWhite,
    backgroundColor: bentoTeal2,
  },
  footerText: {
    pt: 0,
  },
  feeText: {
    pb: 0,
  },
};

interface OfficeFeeLimits {
  code: string;
  softLimit: number;
  hardLimit: number;
}

interface AddServiceOptions {
  addAnother: boolean;
}

const formatUsdWithCommas = (amount: number | string | null | undefined): string => {
  const parsedAmount =
    typeof amount === 'number' ? amount : Number.parseFloat(String(amount ?? ''));

  if (!Number.isFinite(parsedAmount)) {
    return formatUsd(amount);
  }

  return parsedAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function AddServices(props: AddServicesProps) {
  const [loader, setLoader] = useState(true);
  const [toothSelect, setToothSelect] = useState<TeethState>(generateDefaultTeethState());
  const [quadrantSelect, setQuadrantSelect] = useState(defaultQuadrantState);
  const [archSelect, setArchSelect] = useState(defaultArchState);
  const [addServiceButtonIsDisabled, setAddServiceButtonIsDisabled] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [officeFee, setOfficeFee] = useState('');
  const [officeFeeError, setOfficeFeeError] = useState('');
  const [officeFeeText, setOfficeFeeText] = useState('');
  const [officeFeeLimits, setOfficeFeeLimits] = useState<OfficeFeeLimits | null>(null);
  const [confirmOfficeFeeOpen, setConfirmOfficeFeeOpen] = useState(false);
  const [pendingAddServiceOptions, setPendingAddServiceOptions] =
    useState<AddServiceOptions | null>(null);
  const officeFeeLimitRequestIdRef = useRef(0);

  const selectedServiceDetail = useMemo<Service | undefined>(() => {
    if (!selectedService || !props.allServices) {
      return undefined;
    }
    return props.allServices.find((service: Service) => service.code === selectedService.value);
  }, [selectedService, props.allServices]);

  const negotiatedFeeIsAvailable =
    selectedServiceDetail?.price !== null &&
    selectedServiceDetail?.price !== undefined &&
    selectedServiceDetail?.price !== '';
  const negotiatedFeeLabel = (
    selectedServiceDetail?.negotiated_fee_label || 'Negotiated Fee'
  ).replace(/:\s*$/, '');

  const initialize = () => {
    setOfficeFeeText('');
    setLoader(false);
    setAddServiceButtonIsDisabled(false);
  };

  const shouldFetchOfficeFeeLimits = !!(
    selectedService?.value &&
    !selectedServiceDetail?.price &&
    props.portal === constants.PORTALS.DENTIST &&
    props.dentalAccessPoint &&
    props.date
  );

  const fetchOfficeFeeLimits = async (serviceCode: string): Promise<OfficeFeeLimits | null> => {
    if (
      props.portal !== constants.PORTALS.DENTIST ||
      !props.dentalAccessPoint ||
      !props.date ||
      !serviceCode
    ) {
      setOfficeFeeLimits(null);
      return null;
    }

    const requestId = ++officeFeeLimitRequestIdRef.current;

    try {
      const response = await axios.get(`${getUrlPrefix(props.portal)}/dental-service/fee-limits/`, {
        params: {
          dental_access_point: props.dentalAccessPoint,
          date: formatDateForRequest(props.date),
          code: serviceCode,
        },
      });

      const limits = {
        code: serviceCode,
        softLimit: Number(response.data.soft_limit),
        hardLimit: Number(response.data.hard_limit),
      };

      if (requestId === officeFeeLimitRequestIdRef.current) {
        setOfficeFeeLimits(limits);
      }

      return limits;
    } catch (error) {
      if (requestId === officeFeeLimitRequestIdRef.current) {
        setOfficeFeeLimits(null);
      }
      notify(
        (error as { message?: string }).message || 'Unable to load office fee limits.',
        'error'
      );
      return null;
    }
  };

  const resetOfficeFeeValidationState = () => {
    officeFeeLimitRequestIdRef.current += 1;
    setOfficeFeeLimits(null);
    setConfirmOfficeFeeOpen(false);
    setPendingAddServiceOptions(null);
  };

  const convertAsClaimExtract = (service: ServiceData): Promise<ClaimExtract> => {
    return axios
      .post('mast-alto/dental-service/as-claim-extract/', service)
      .then((response) => response.data.data);
  };

  const convertAsServiceData = (serviceExtract: ClaimExtract): Promise<{ data: ServiceData[] }> => {
    return axios
      .post('mast-alto/dental-service/as-service-data/', { services: [serviceExtract] })
      .then((response) => response.data)
      .catch((error: { message: string }) => {
        notify(error.message, 'error');
        throw error;
      });
  };

  const { editedService, usesServiceConverting } = props;

  useEffect(() => {
    initialize();
    resetOfficeFeeValidationState();
    if (editedService && usesServiceConverting) {
      convertAsServiceData(editedService as unknown as ClaimExtract).then((response) => {
        setQuadrantSelect({ ...defaultQuadrantState, ...response.data[0]?.quadrant_select });
        setArchSelect({ ...defaultArchState, ...response.data[0]?.arch_select });
        setToothSelect({ ...generateDefaultTeethState(), ...response.data[0]?.tooth_select });
        if (props.allServices) {
          const foundService = props.allServices.find(
            (item: Service) => item.code === response.data[0]?.code
          );
          setSelectedService({
            value: response.data[0]?.code,
            label: `${response.data[0]?.code} - ${foundService?.description || ''}`,
          });
        }
        setOfficeFee(response.data[0]?.office_fee || '');
      });
    }
    if (editedService && !usesServiceConverting) {
      if (editedService.quadrant_select) {
        setQuadrantSelect({ ...defaultQuadrantState, ...editedService.quadrant_select });
      }
      if (editedService.arch_select) {
        setArchSelect({ ...defaultArchState, ...editedService.arch_select });
      }
      if (editedService.tooth_select) {
        setToothSelect({ ...generateDefaultTeethState(), ...editedService.tooth_select });
      }
      if (props.allServices) {
        const foundService = props.allServices.find(
          (item: Service) => item.code === editedService.code
        );
        setSelectedService({
          value: editedService.code,
          label: `${editedService.code} - ${foundService?.description || ''}`,
        });
      }
      if (editedService.office_fee) {
        setOfficeFee(editedService.office_fee);
      }
    }
  }, [editedService, props.allServices]);

  useEffect(() => {
    resetOfficeFeeValidationState();

    if (!shouldFetchOfficeFeeLimits || !selectedService?.value) {
      return;
    }

    void fetchOfficeFeeLimits(selectedService.value);
  }, [
    selectedService?.value,
    selectedServiceDetail?.price,
    props.portal,
    props.dentalAccessPoint,
    props.date,
  ]);

  const defaultOpts: AddServiceOptions = { addAnother: false };

  const validateOfficeFeeAmount = async (
    service: Service,
    opts: AddServiceOptions,
    skipSoftLimitConfirmation = false
  ) => {
    if (
      props.portal !== constants.PORTALS.DENTIST ||
      !props.dentalAccessPoint ||
      !props.date ||
      service.price ||
      !selectedService?.value
    ) {
      return true;
    }

    const officeFeeAmount = Number.parseFloat(officeFee);
    if (!Number.isFinite(officeFeeAmount)) {
      return true;
    }

    const limits =
      officeFeeLimits?.code === selectedService.value
        ? officeFeeLimits
        : await fetchOfficeFeeLimits(selectedService.value);

    if (!limits) {
      setAddServiceButtonIsDisabled(false);
      return false;
    }

    if (officeFeeAmount > limits.hardLimit) {
      notify(
        `We are unable to process code ${selectedService.value} for ${formatUsdWithCommas(
          officeFeeAmount
        )}. Please check the amount and try again, or contact Bento for assistance.`,
        'error'
      );
      setAddServiceButtonIsDisabled(false);
      return false;
    }

    if (!skipSoftLimitConfirmation && officeFeeAmount > limits.softLimit) {
      setPendingAddServiceOptions(opts);
      setConfirmOfficeFeeOpen(true);
      setAddServiceButtonIsDisabled(false);
      return false;
    }

    return true;
  };

  const addService = async (
    opts: AddServiceOptions = defaultOpts,
    skipSoftLimitConfirmation = false
  ) => {
    const { addService: addServiceFn, editService: editServiceFn, usesServiceConverting } = props;
    if (selectedService) {
      setAddServiceButtonIsDisabled(true);
      const selectedServiceData = props.allServices.find(
        (service: Service) => service.code === selectedService.value
      );

      if (!selectedServiceData) {
        setServicesError('Service not found.');
        setAddServiceButtonIsDisabled(false);
        return;
      }

      // Submission preparation changes price and selection fields. Work from a copy so a rejected
      // submission does not mutate the service details still displayed in the dialog.
      const service = deepCopy(selectedServiceData);

      // Validate that office fee exists when required or when the service has no price
      if ((props.requireOfficeFee || !service.price) && !officeFee) {
        setOfficeFeeError(constants.VALIDATION.REQUIRED);
        setAddServiceButtonIsDisabled(false);
        return;
      }

      if (!(await validateOfficeFeeAmount(service, opts, skipSoftLimitConfirmation))) {
        return;
      }

      service.tooth_select = toothSelect;
      service.quadrant_select = quadrantSelect;
      service.arch_select = archSelect;
      service.service = service.service || 'N/A';
      service.is_final = true;
      // If the user overrides existing service price
      if (officeFee) {
        service.price = null;
        service.office_fee = officeFee;
      }
      setLoader(true);

      if (!usesServiceConverting) {
        if (editedService) {
          editServiceFn?.(service);
          setLoader(false);
          setAddServiceButtonIsDisabled(false);
        } else {
          addServiceFn?.(service, opts.addAnother)
            .then(() => {
              if (opts.addAnother) {
                setSelectedService(null);
                setQuadrantSelect(defaultQuadrantState);
                setArchSelect(defaultArchState);
                setToothSelect(generateDefaultTeethState());
                setOfficeFee('');
                resetOfficeFeeValidationState();
              }
              notify(`Added service ${selectedService.value}`, 'success');
            })
            .catch((error: { message: string }) => {
              notify(error.message, 'error');
            })
            .finally(() => {
              setLoader(false);
              setAddServiceButtonIsDisabled(false);
            });
        }
      } else {
        convertAsClaimExtract(service)
          .then((resultService) => {
            if (editedService) {
              editServiceFn?.(resultService);
              props.handleClose?.();
            } else {
              addServiceFn?.(resultService, opts.addAnother)?.then(() => {
                if (opts.addAnother) {
                  setSelectedService(null);
                  setQuadrantSelect(defaultQuadrantState);
                  setArchSelect(defaultArchState);
                  setToothSelect(generateDefaultTeethState());
                  setOfficeFee('');
                  resetOfficeFeeValidationState();
                } else {
                  props.handleClose?.();
                }
                notify(`Added service ${selectedService.value}`, 'success');
              });
            }
          })
          .catch((error: { message: string }) => {
            notify(error.message, 'error');
          })
          .finally(() => {
            setLoader(false);
            setAddServiceButtonIsDisabled(false);
          });
      }
    } else {
      setServicesError('Please select a service.');
    }
  };

  const addAnotherService = async () => {
    await addService({ addAnother: true });
  };

  const onToothSelection = (tooth: number | string) => {
    setToothSelect((prevToothSelect) => {
      const newToothSelect = deepCopy(prevToothSelect);
      newToothSelect[tooth].selected = !prevToothSelect[tooth].selected;
      if (!newToothSelect[tooth].selected) {
        newToothSelect[tooth] = deepCopy(DEFAULT_TOOTH_STATE);
      }
      return newToothSelect;
    });
  };

  const onSurfaceSelection = (num: number | string, surface: string) => {
    const surfaces: Record<string, string> = {
      D: 'distal',
      O: 'occlusal',
      B: 'buccal',
      M: 'mesial',
      L: 'lingual',
    };

    setToothSelect((prevToothSelect) => {
      const newToothSelect = deepCopy({ ...generateDefaultTeethState(), ...prevToothSelect });
      const previousToothSelectCopy = deepCopy({
        ...generateDefaultTeethState(),
        ...prevToothSelect,
      });
      const surfaceName = surfaces[surface] || '';
      newToothSelect[num].selected = true;
      newToothSelect[num].surface_select = {
        ...newToothSelect[num].surface_select,
        [surfaceName]: {
          selected:
            !previousToothSelectCopy[num]?.surface_select[
              surfaceName as keyof (typeof previousToothSelectCopy)[number]['surface_select']
            ]?.selected,
        },
      };
      return newToothSelect;
    });
  };

  const onQuadrantSelection = (
    quadrant: 'upper_left' | 'upper_right' | 'lower_left' | 'lower_right'
  ) => {
    setQuadrantSelect((prevQuadrantSelect) => {
      const newQuadrantSelect = deepCopy(prevQuadrantSelect);
      newQuadrantSelect[quadrant].selected = !prevQuadrantSelect[quadrant].selected;
      return newQuadrantSelect;
    });
  };

  const onArchSelection = (arch: 'upper' | 'lower') => {
    setArchSelect((prevArchSelect) => {
      const newArchSelect = deepCopy(prevArchSelect);
      newArchSelect[arch].selected = !prevArchSelect[arch].selected;
      return newArchSelect;
    });
  };

  const confirmOfficeFeeAmount = async () => {
    const nextOptions = pendingAddServiceOptions || defaultOpts;
    setConfirmOfficeFeeOpen(false);
    setPendingAddServiceOptions(null);
    await addService(nextOptions, true);
  };

  if (loader) {
    return <Loader />;
  }

  return (
    <Grid container>
      <Grid size={12}>
        <ServicesDropdown
          services={props.allServices}
          selectedService={selectedService}
          handleChange={setSelectedService}
          isInDialog={props.isInDialog}
          disabled={props.servicesLoading}
        />
        {servicesError && <Typography color="error">{servicesError}</Typography>}
        {selectedServiceDetail?.fee_entry_reason_text && (
          <Typography color="error">{selectedServiceDetail.fee_entry_reason_text}</Typography>
        )}
      </Grid>
      {props.showFrequency && props.portal && props.date && (
        <Grid size={12}>
          <ServiceFrequencyInfo
            portal={props.portal}
            selectedService={selectedService}
            patient={props.patient}
            date={new Date(props.date)}
          />
        </Grid>
      )}
      <Grid container size={12}>
        <Grid size={12}>
          <Stack direction="column" spacing={0}>
            <Stack direction="row" spacing={0}>
              <Stack
                direction="row"
                sx={{
                  borderRight: 1,
                  borderRightColor: '#e0e0e0',
                  borderBottom: 1,
                  borderBottomColor: '#e0e0e0',
                  paddingBottom: 1.3,
                  paddingRight: 2,
                }}
                spacing={0}
              >
                {(function () {
                  const quadrants = [];
                  for (let i = 1; i <= 8; i++) {
                    quadrants.push(
                      <ToothQuadrant
                        toothPositionTop={false}
                        num={i}
                        onSurfaceClick={onSurfaceSelection}
                        onToothClick={onToothSelection}
                        key={`teeth-row-1-tooth${i}`}
                        tooth_select={toothSelect[i] || false}
                      />
                    );
                  }
                  return quadrants;
                })()}
              </Stack>
              <Stack
                direction="row"
                sx={{
                  borderBottom: 1,
                  borderBottomColor: '#e0e0e0',
                  paddingBottom: 1.3,
                  paddingLeft: 2,
                }}
                spacing={0}
              >
                {(function () {
                  const quadrants = [];
                  for (let i = 9; i <= 16; i++) {
                    quadrants.push(
                      <ToothQuadrant
                        toothPositionTop={false}
                        num={i}
                        onSurfaceClick={onSurfaceSelection}
                        onToothClick={onToothSelection}
                        key={`teeth-row-2-tooth${i}`}
                        tooth_select={toothSelect[i] || false}
                      />
                    );
                  }
                  return quadrants;
                })()}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Stack
                direction="row"
                sx={{
                  borderRight: 1,
                  borderRightColor: '#e0e0e0',
                  paddingTop: 1.3,
                  paddingRight: 2,
                }}
                spacing={0}
              >
                {(function () {
                  const quadrants = [];
                  for (let i = 17; i <= 24; i++) {
                    quadrants.push(
                      <ToothQuadrant
                        toothPositionTop
                        num={i}
                        onSurfaceClick={onSurfaceSelection}
                        onToothClick={onToothSelection}
                        key={`teeth-row-3-tooth${i}`}
                        tooth_select={toothSelect[i] || false}
                      />
                    );
                  }
                  return quadrants;
                })()}
              </Stack>
              <Stack direction="row" sx={{ paddingLeft: 2, paddingTop: 1.3 }} spacing={0}>
                {(function () {
                  const quadrants = [];
                  for (let i = 25; i <= 32; i++) {
                    quadrants.push(
                      <ToothQuadrant
                        toothPositionTop
                        num={i}
                        onSurfaceClick={onSurfaceSelection}
                        onToothClick={onToothSelection}
                        key={`teeth-row-4-tooth${i}`}
                        tooth_select={toothSelect[i] || false}
                      />
                    );
                  }
                  return quadrants;
                })()}
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={8}>
          <Stack direction="column" spacing={0} sx={{ marginTop: 2 }}>
            <Stack direction="row" spacing={0}>
              <Stack
                direction="row"
                sx={{
                  borderRight: 1,
                  borderRightColor: '#e0e0e0',
                  borderBottom: 1,
                  borderBottomColor: '#e0e0e0',
                  paddingBottom: 1.3,
                  paddingRight: 2,
                }}
                spacing={0}
              >
                {['A', 'B', 'C', 'D', 'E'].map((i) => (
                  <ToothQuadrant
                    toothPositionTop={false}
                    num={i}
                    onSurfaceClick={onSurfaceSelection}
                    onToothClick={onToothSelection}
                    key={`teeth-row-3-1-tooth${i}`}
                    tooth_select={toothSelect[i] || false}
                  />
                ))}
              </Stack>
              <Stack
                direction="row"
                sx={{
                  borderBottom: 1,
                  borderBottomColor: '#e0e0e0',
                  paddingBottom: 1.3,
                  paddingLeft: 2,
                }}
                spacing={0}
              >
                {['F', 'G', 'H', 'I', 'J'].map((i) => (
                  <ToothQuadrant
                    toothPositionTop={false}
                    num={i}
                    onSurfaceClick={onSurfaceSelection}
                    onToothClick={onToothSelection}
                    key={`teeth-row-3-2-tooth${i}`}
                    tooth_select={toothSelect[i] || false}
                  />
                ))}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Stack
                direction="row"
                sx={{
                  borderRight: 1,
                  borderRightColor: '#e0e0e0',
                  paddingTop: 1.3,
                  paddingRight: 2,
                }}
                spacing={0}
              >
                {['T', 'S', 'R', 'Q', 'P'].map((i) => (
                  <ToothQuadrant
                    toothPositionTop
                    num={i}
                    onSurfaceClick={onSurfaceSelection}
                    onToothClick={onToothSelection}
                    key={`teeth-row-4-1-tooth${i}`}
                    tooth_select={toothSelect[i] || false}
                  />
                ))}
              </Stack>
              <Stack
                direction="row"
                sx={{
                  paddingTop: 1.3,
                  paddingLeft: 2,
                }}
                spacing={0}
              >
                {['O', 'N', 'M', 'L', 'K'].map((i) => (
                  <ToothQuadrant
                    toothPositionTop
                    num={i}
                    onSurfaceClick={onSurfaceSelection}
                    onToothClick={onToothSelection}
                    key={`teeth-row-4-2-tooth${i}`}
                    tooth_select={toothSelect[i] || false}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={4}>
          <Box sx={styles.quadrants}>
            <Box
              sx={{
                ...styles.quadrant,
                ...(quadrantSelect.upper_right.selected ? styles.selected : {}),
              }}
              onClick={() => onQuadrantSelection('upper_right')}
            >
              <span>
                Upper Right
                <br />
                Quadrant (10)
              </span>
            </Box>
            <Box
              sx={{
                ...styles.quadrant,
                ...(quadrantSelect.upper_left.selected ? styles.selected : {}),
              }}
              onClick={() => onQuadrantSelection('upper_left')}
            >
              <span>
                Upper Left
                <br />
                Quadrant (20)
              </span>
            </Box>
            <Box
              sx={{
                ...styles.quadrant,
                ...(quadrantSelect.lower_right.selected ? styles.selected : {}),
              }}
              onClick={() => onQuadrantSelection('lower_right')}
            >
              <span>
                Lower Right
                <br />
                Quadrant (40)
              </span>
            </Box>
            <Box
              sx={{
                ...styles.quadrant,
                ...(quadrantSelect.lower_left.selected ? styles.selected : {}),
              }}
              onClick={() => onQuadrantSelection('lower_left')}
            >
              <span>
                Lower Left
                <br />
                Quadrant (30)
              </span>
            </Box>
          </Box>
          <Box sx={styles.arches}>
            <Box
              sx={{
                ...styles.arch,
                ...(archSelect.upper.selected ? styles.selected : {}),
              }}
              onClick={() => onArchSelection('upper')}
            >
              <span>Maxillary Arch (01)</span>
            </Box>
            <Box
              sx={{
                ...styles.arch,
                ...(archSelect.lower.selected ? styles.selected : {}),
              }}
              onClick={() => onArchSelection('lower')}
            >
              <span>Mandibular Arch (02)</span>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container size={12} alignItems={props.requireOfficeFee ? 'center' : undefined}>
        {selectedService && selectedServiceDetail && (
          <>
            {props.requireOfficeFee && (
              <Grid sx={{ ...styles.feeText, mr: 2 }} size="auto">
                <Typography>
                  {negotiatedFeeLabel}:{' '}
                  {negotiatedFeeIsAvailable ? formatUsd(selectedServiceDetail.price) : 'N/A'}
                </Typography>
              </Grid>
            )}
            <Grid sx={styles.feeText} size={4}>
              {props.requireOfficeFee || !selectedServiceDetail.price ? (
                <UsdTextField
                  id="office-fee"
                  name="office-fee"
                  value={officeFee}
                  label={officeFeeText || 'Office Fee'}
                  onChange={(e) => {
                    setOfficeFee(e.target.value);
                    setOfficeFeeError('');
                    setConfirmOfficeFeeOpen(false);
                    setPendingAddServiceOptions(null);
                    setAddServiceButtonIsDisabled(false);
                  }}
                  fullWidth={false}
                  error={!!officeFeeError}
                  helperText={officeFeeError}
                />
              ) : (
                <Typography>
                  {selectedServiceDetail.negotiated_fee_label} ${selectedServiceDetail.price}
                </Typography>
              )}
            </Grid>
          </>
        )}

        {officeFeeText && (
          <Grid size={12}>
            <TypographyError>{officeFeeText}</TypographyError>
          </Grid>
        )}
        {selectedServiceDetail?.fee_entry_footer_text && (
          <Grid sx={styles.footerText} size={12}>
            <Typography>{selectedServiceDetail.fee_entry_footer_text}</Typography>
          </Grid>
        )}
      </Grid>
      <Grid size={12}>
        <Divider sx={{ marginBottom: 2 }} />
        <Stack direction="row">
          <PrimaryButton
            id="btn-add-service"
            onClick={addService}
            disabled={addServiceButtonIsDisabled}
            sx={{ minWidth: 150 }}
          >
            Done
          </PrimaryButton>
          {!editedService && (
            <Button
              id="btn-add-another-service"
              color="primary"
              startIcon={<BentoIcon name="circle-plus" />}
              onClick={addAnotherService}
              sx={{ minWidth: 150 }}
              variant="outlined"
            >
              Add another service
            </Button>
          )}
        </Stack>
      </Grid>
      <ConfirmDialog
        open={confirmOfficeFeeOpen}
        onClose={() => {
          setConfirmOfficeFeeOpen(false);
          setPendingAddServiceOptions(null);
        }}
        title={`Please confirm your office fee for code ${selectedService?.value}.`}
        onConfirm={confirmOfficeFeeAmount}
        confirmText={`${formatUsdWithCommas(officeFee)} is the correct amount`}
        cancelText="Enter another amount"
        buttonComponent={Button}
        disableAutoFocus
      >
        <Typography>Please confirm your office fee for code {selectedService?.value}.</Typography>
      </ConfirmDialog>
    </Grid>
  );
}

export default AddServices;
